import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Switch } from './ui/switch';
import { Cookie, X, Settings } from 'lucide-react';
import { registerAnalyticsSession, syncAnalyticsConsent } from '../lib/analytics';
import { sanitizeRecordingUrl } from '../lib/urlPrivacy';

const COOKIE_CONSENT_KEY = 'mid_cookie_consent';
// Y-09: '1.0' (and unversioned) was the old banner, analytics pre-ticked and no
// session-recording disclosure. Only consent stored at this version counts.
const COOKIE_CONSENT_VERSION = '2';
const POSTHOG_EU_HOST = 'https://eu.i.posthog.com';
// Map home (docs/ANALYTICS.md): buyer-only, no ids or coordinates.
export const MAP_EVENTS = new Set([
  'map_opened', 'map_pin_selected', 'map_sheet_action', 'map_route_started', 'map_labels_toggled', 'map_menu_opened',
]);
const FUNNEL_EVENTS = new Set([
  'site_session_started', 'dna_started', 'dna_step_viewed', 'dna_account_shown', 'dna_account_done', ...MAP_EVENTS,
]);

const isAnalyticsUrl = (value) => typeof value === 'string' && /(?:[a-z][a-z\d+.-]*:\/\/|^\/\/)/i.test(value);

function sanitizeAnalyticsProperties(properties) {
  if (Array.isArray(properties)) return properties.filter((value) => !isAnalyticsUrl(value))
    .map((value) => value && typeof value === 'object' ? sanitizeAnalyticsProperties(value) : value);
  return Object.fromEntries(Object.entries(properties || {}).filter(([key, value]) =>
    !/^\$(current_url|pathname|host|referrer|referring_domain|title|el_text)$|^\$(initial_|elements|session_entry_)/.test(key)
    && !isAnalyticsUrl(value),
  ).map(([key, value]) => [key, value && typeof value === 'object' ? sanitizeAnalyticsProperties(value) : value]));
}

// Errors keep their stack for debugging; messages lose emails and query strings, which can hold tokens.
function sanitizeExceptionList(list) {
  return JSON.parse(JSON.stringify(list || [])
    .replace(/[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g, '[email]')
    .replace(/(https?:\/\/[^\s"?#]+)[?#][^\s"]*/g, '$1'));
}

// /property/:shareToken is a working share link, so those segments never leave the browser.
const SECRET_PATH_PARENT = /^(property|intermediary|buyers)$/i;
const STATIC_CHILD = /^(login|setup-password|reset-password|dashboard)$/i;
const ID_SEGMENT = /^(?:[a-z]+_[\da-z]{6,}|[\da-f]{8,}|[\da-f-]{32,})$/i;

function sanitizePageviewPath(pathname) {
  return String(pathname || '/').split('/')
    .map((segment, i, all) => (segment && ((SECRET_PATH_PARENT.test(all[i - 1] || '') && !STATIC_CHILD.test(segment)) || ID_SEGMENT.test(segment)) ? ':id' : segment))
    .join('/');
}

// The page and nothing else: no query, hash, title, referrer or previous path.
function sanitizePageview(event) {
  const properties = event.properties || {};
  const url = sanitizeRecordingUrl(properties.$current_url || '');
  const origin = /^[a-z][a-z\d+.-]*:\/\/[^/]+/i.exec(url)?.[0] || '';
  const pathname = sanitizePageviewPath(url.slice(origin.length) || properties.$pathname);
  const kept = sanitizeAnalyticsProperties(Object.fromEntries(
    Object.entries(properties).filter(([key]) => !key.startsWith('$prev_pageview')),
  ));
  return { ...event, properties: {
    ...kept, $current_url: `${origin}${pathname}`, $pathname: pathname, ...(properties.$host ? { $host: properties.$host } : {}),
  } };
}

function sanitizeFunnelEvent(event) {
  if (event?.event === '$snapshot') return event;
  if (event?.event === '$pageview') return sanitizePageview(event);
  if (event?.event === '$exception') {
    return { ...event, properties: {
      ...sanitizeAnalyticsProperties(event.properties),
      $exception_list: sanitizeExceptionList(event.properties?.$exception_list),
    } };
  }
  if (!FUNNEL_EVENTS.has(event?.event) && event?.event !== '$identify') return null;
  return {
    ...event,
    properties: sanitizeAnalyticsProperties(event.properties),
    ...(event.$set ? { $set: sanitizeAnalyticsProperties(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: sanitizeAnalyticsProperties(event.$set_once) } : {}),
  };
}

function postHogLoaded(sdk, syncConsent = true) {
  sdk.__midAnalyticsLoaded = true;
  if (syncConsent) syncAnalyticsConsent();
  window.dispatchEvent(new CustomEvent('mid:posthog-loaded', { detail: sdk }));
  registerAnalyticsSession(sdk);
}

// I-04: never recorded, not even masked (income, savings, credit request).
const POSTHOG_BLOCKED = [
  '[data-testid="credit-sheet"]',
  '[data-testid^="mortgage-"]',
  '[data-testid="budget-reconciliation-card"]',
  '[data-testid="intermediary-readiness-queue"]',
  '[data-testid="admin-readiness-queue"]',
  '[data-testid="credit-request-flow"]',
].join(', ');

/**
 * I-04: load miD's own PostHog project only after the banner's analytics yes.
 * Key and host are build-time env (REACT_APP_POSTHOG_KEY, REACT_APP_POSTHOG_HOST,
 * host defaults to the EU cloud). No key -> PostHog never loads.
 * Same queue stub the official snippet builds; array.js drains window.posthog._i.
 * Recording excludes request bodies, headers and console output.
 */
export function loadPostHogAfterConsent() {
  const key = process.env.REACT_APP_POSTHOG_KEY;
  if (!key) return;
  if (window.posthog) {
    if (!Array.isArray(window.posthog) && typeof window.posthog.capture === 'function') {
      postHogLoaded(window.posthog, false);
    }
    return;
  }
  const host = process.env.REACT_APP_POSTHOG_HOST || POSTHOG_EU_HOST;
  const queue = [];
  queue.people = [];
  queue._i = [[key, {
    api_host: host,
    person_profiles: 'identified_only',
    autocapture: false,
    capture_pageview: 'history_change',
    capture_pageleave: false,
    capture_exceptions: { capture_unhandled_errors: true, capture_unhandled_rejections: true, capture_console_errors: false },
    disable_capture_url_hashes: true,
    enable_recording_console_log: false,
    mask_personal_data_properties: true,
    custom_personal_data_properties: ['token', 'code', 'claim', 'claim_id', 'session_id', 'access_token', 'refresh_token', 'id_token', 'state'],
    before_send: sanitizeFunnelEvent,
    loaded: postHogLoaded,
    session_recording: {
      maskAllInputs: true,
      recordBody: false,
      recordHeaders: false,
      maskTextSelector: '[data-testid="dna-v2-chat-panel"]',
      blockSelector: POSTHOG_BLOCKED,
      // The replay URL masker also uses this callback for hrefs.
      maskCapturedNetworkRequestFn: (request) => ({ ...request, name: sanitizeRecordingUrl(request.name) }),
    },
  }, 'posthog']];
  window.posthog = queue;
  const script = document.createElement('script');
  script.async = true;
  script.crossOrigin = 'anonymous';
  script.src = host.replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js';
  document.head.appendChild(script);
}

export function CookieConsentBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be changed
    functional: true,
    analytics: false // I-04: consent is an explicit yes, never pre-ticked
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = readCookieConsent();
    if (consent?.analytics === true) loadPostHogAfterConsent();
    if (!consent) {
      // Small delay before showing banner for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (consentData) => {
    const previous = readCookieConsent();
    const data = {
      ...consentData,
      timestamp: new Date().toISOString(),
      version: COOKIE_CONSENT_VERSION
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(data));
    if (data.analytics && previous?.analytics !== data.analytics) {
      window.posthog?.opt_in_capturing?.({ captureEventName: false });
      window.posthog?.startSessionRecording?.();
    }
    syncAnalyticsConsent(data);
    if (data.analytics) loadPostHogAfterConsent();
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptAll = () => {
    saveConsent({
      essential: true,
      functional: true,
      analytics: true,
      accepted: true
    });
  };

  const handleRejectNonEssential = () => {
    saveConsent({
      essential: true,
      functional: false,
      analytics: false,
      accepted: false
    });
  };

  const handleSavePreferences = () => {
    saveConsent({
      ...preferences,
      accepted: preferences.functional || preferences.analytics
    });
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed bottom-0 md:bottom-0 left-0 right-0 z-[48] p-2 sm:p-6 mb-[72px] md:mb-0 cookie-banner"
      >
        <Card className="max-w-4xl mx-auto shadow-2xl border-0 bg-[var(--brand-surface,#fff)] text-[var(--brand-ink,#1A1A1A)]">
          <CardContent className="p-3 sm:p-6">
            {!showSettings ? (
              // Main Banner - Compacto em mobile
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 items-start sm:items-center">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 sm:mb-2">
                    <Cookie className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    <h3 className="font-semibold text-base sm:text-lg">Utilizamos cookies</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-[var(--brand-muted,#555550)] line-clamp-2 sm:line-clamp-none">
                    Usamos cookies essenciais para o miD funcionar. Com a tua autorização, usamos também o PostHog (servidores na União Europeia) para perceber como usas o miD, incluindo gravações de sessão em que o que escreves fica oculto.{' '}
                    <Link to="/cookies" className="text-[var(--mid-primary-dark)] hover:underline">
                      Saber mais
                    </Link>
                  </p>
                </div>
                
                {/* Mobile: botões horizontais em linha única */}
                <div className="flex flex-row sm:flex-row gap-2 w-full sm:w-auto">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowSettings(true)}
                    aria-label="Configurar"
                    className="flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-4"
                  >
                    <Settings className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Configurar</span>
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={handleRejectNonEssential}
                    className="flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-4 bg-[var(--mid-primary-dark)] hover:bg-[var(--mid-primary-dark)]"
                  >
                    Rejeitar
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={handleAcceptAll}
                    className="flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-4 bg-[var(--mid-primary-dark)] hover:bg-[var(--mid-primary-dark)]"
                  >
                    Aceitar
                  </Button>
                </div>
              </div>
            ) : (
              // Settings Panel
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold text-lg">Configurar cookies</h3>
                  </div>
                  <Button
                    aria-label="Fechar"
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowSettings(false)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <div className="space-y-4 mb-6">
                  {/* Essential */}
                  <div className="flex items-center justify-between p-3 bg-[var(--brand-surface-2,#F3F3F0)] rounded-lg">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-[var(--mid-secondary)] rounded-full"></div>
                        <p className="font-medium text-sm">Cookies Essenciais</p>
                      </div>
                      <p className="text-xs text-[var(--brand-muted,#555550)] mt-1">
                        Necessários para o funcionamento da plataforma. Sempre ativos.
                      </p>
                    </div>
                    <Switch checked={true} disabled />
                  </div>

                  {/* Functional */}
                  <div className="flex items-center justify-between p-3 bg-[var(--brand-surface-2,#F3F3F0)] rounded-lg">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <p className="font-medium text-sm">Cookies Funcionais</p>
                      </div>
                      <p className="text-xs text-[var(--brand-muted,#555550)] mt-1">
                        Melhoram a funcionalidade e personalização.
                      </p>
                    </div>
                    <Switch 
                      checked={preferences.functional}
                      onCheckedChange={(checked) => setPreferences(p => ({ ...p, functional: checked }))}
                    />
                  </div>

                  {/* Analytics */}
                  <div className="flex items-center justify-between p-3 bg-[var(--brand-surface-2,#F3F3F0)] rounded-lg">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                        <p className="font-medium text-sm">Cookies Analíticos</p>
                      </div>
                      <p className="text-xs text-[var(--brand-muted,#555550)] mt-1">
                        Estatísticas de utilização e gravações de sessão com o PostHog, na União Europeia. O que escreves e os teus dados financeiros nunca ficam visíveis nas gravações.
                      </p>
                    </div>
                    <Switch 
                      checked={preferences.analytics}
                      onCheckedChange={(checked) => setPreferences(p => ({ ...p, analytics: checked }))}
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 justify-end">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleRejectNonEssential}
                  >
                    Rejeitar não essenciais
                  </Button>
                  <Button 
                    size="sm"
                    onClick={handleSavePreferences}
                    className="bg-[var(--mid-primary-dark)] hover:bg-[var(--mid-primary-dark)]"
                  >
                    Guardar preferências
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
}

// Hook to get current cookie consent
export function useCookieConsent() {
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    setConsent(readCookieConsent());
  }, []);

  return consent;
}

// Function to check if a specific cookie type is allowed
export function isCookieAllowed(type) {
  return readCookieConsent()?.[type] === true;
}

/** Stored consent, or null when absent, unparseable or from an older banner version. */
export function readCookieConsent() {
  try {
    const consent = JSON.parse(localStorage.getItem(COOKIE_CONSENT_KEY));
    return consent?.version === COOKIE_CONSENT_VERSION ? consent : null;
  } catch (e) {
    return null;
  }
}
